import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OutsideClickHandler } from '@/ui/outside-click-handler/outside-click-handler';
import { styled } from 'goober';

import { Loader } from '@/components/loader/loader';
import type { ClientOption } from '@/types';
import { AsyncSelectWithList } from '@/ui/select/async/async-select-with-list';
import { OptionWithLogo } from '@/ui/select/async/components/option/option-with-logo';
import type { CustomAsyncSelectProps } from '@/ui/select/async/use-select-type';
import { Header } from '@/ui/typography/widgets';
import {
  EmptyStateWidget,
  type EmptyStateWidgetProps,
} from '@/ui/widget/empty-state-widget';
import { Widget, type WidgetProps } from '@/ui/widget/widget';

import { WIDGET_CONTAINER_GAP } from '../../../../components/widgets/text-widget/text-widget';
import { SelectedOptionsListAsLogo } from '../../../../ui/select/async/components/selected-options-list/selected-options-list-as-logo';
import { mapOrganizationClientsToOptions } from '../../helpers/map-clients-to-options';
import { useCanEditCompany } from '../../hooks/use-can-edit-company';

import { WIDGET_HEADER_MARGIN, WIDGET_ROW_1_HEIGHT } from '../utils/constants';
import { companyIdState } from '../company.state';
import { WidgetLoaderContainer } from '../widget-loader-container';
import { useCompanyClients } from './use-company-clients.query';
import type { CompanyClient } from '@/api/v4/organization-clients.api';
import { useEditCompanyClients } from './use-edit-company-clients.mutation';
import {
  hasResolvedClientOptionId,
  mergeClientOptions,
  resolveClientOptionId,
} from './client-option.helpers';
import { fetchClients } from '@/api/v4/client.api';
import { mapClientsToOptions } from '@/api/v4/mappers/client-option.mapper';
import { Small } from '@/ui/typography/across-platform';
import { useAtomValue } from 'jotai';

interface ClientsWidgetProps extends WidgetProps {
  defaultValue?: CompanyClient[] | undefined;
  emptyStateOptions?: Omit<EmptyStateWidgetProps, 'onClick'>;
  optionsListLimit?: number;
  onSave?: (value: string[]) => void;
  companyId?: string;
}

export function ClientsWidget({
  height,
  emptyStateOptions,
  optionsListLimit = 0,
  className,
  companyId,
  editable = true,
  ...widgetProps
}: ClientsWidgetProps) {
  const { t } = useTranslation(['companies', 'default']);
  const [isEditMode, setIsEditMode] = useState(false);
  const { mutateAsync: editOrganizationClients, isPending } =
    useEditCompanyClients();

  const companyOverviewId = useAtomValue(companyIdState);

  const { data: companyClients = [], isLoading } = useCompanyClients(companyId);

  const clients = useMemo(
    () => (companyClients ? companyClients : []),
    [companyClients],
  );

  const [draftOptions, setDraftOptions] = useState<ClientOption[]>([]);

  const listRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [maxHeight, setMaxHeight] = useState<string>();

  const canEditCompany = useCanEditCompany();
  const disabled = !canEditCompany;

  useEffect(() => {
    if (isEditMode) {
      setDraftOptions(mapOrganizationClientsToOptions(clients));
    }
  }, [clients, isEditMode]);

  useEffect(() => {
    if (clients.length <= optionsListLimit) {
      setMaxHeight(undefined);
      return;
    }

    const item: HTMLDivElement | null | undefined =
      listRef.current?.querySelector('[data-name="options-list-item"]');

    const ITEM_GAP = 16;
    const ITEMS_PER_ROW = 3;
    const height =
      ((item?.offsetHeight ?? 0) / ITEMS_PER_ROW + ITEM_GAP) *
        (optionsListLimit - 1) -
      ITEM_GAP;

    setMaxHeight(`${height}px`);
  }, [optionsListLimit, clients]);

  const handleOnConfirm = useCallback(async () => {
    if (!isEditMode || !companyOverviewId || !editable) return;

    if (draftOptions.some(option => !hasResolvedClientOptionId(option))) {
      return;
    }

    setIsEditMode(false);
    if (
      draftOptions.length === clients.length &&
      draftOptions.every(
        (item, index) => resolveClientOptionId(item) === clients[index].id,
      )
    ) {
      return;
    }

    await editOrganizationClients(
      draftOptions.map(option => ({
        source: option.source,
        id: resolveClientOptionId(option),
      })),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyOverviewId,
    clients,
    isEditMode,
    editOrganizationClients,
    draftOptions,
  ]);

  if (!companyOverviewId && !companyId) {
    return null;
  }

  if (isPending || isLoading) {
    return (
      <WidgetLoaderContainer height={WIDGET_ROW_1_HEIGHT} width={439}>
        <Loader />
      </WidgetLoaderContainer>
    );
  }

  if (
    !isEditMode &&
    emptyStateOptions &&
    !disabled &&
    !clients.length &&
    editable
  ) {
    return (
      <EmptyStateWidget
        className={className}
        height={height}
        onEditClick={() => setIsEditMode(true)}
        {...emptyStateOptions}
      />
    );
  }

  const handleOnExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const setOptions: CustomAsyncSelectProps['setOptions'] = async search => {
    const clients = await fetchClients({
      search,
    });

    return mapClientsToOptions(clients);
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOnConfirm}>
      <Container
        className={className}
        header={<HeaderContainer>{t`clients.header`}</HeaderContainer>}
        height={height}
        isEditMode={isEditMode}
        onEditClick={() => setIsEditMode(true)}
        disabled={disabled}
        showExpandButton={!isEditMode && clients.length > optionsListLimit}
        labelExpand={t`default:showAll`}
        labelCollapse={t`default:showLess`}
        showMore={isExpanded}
        showEditButton={editable}
        onReadMoreClick={handleOnExpand}
        {...widgetProps}
      >
        {!isEditMode && !editable && !clients?.length && (
          <Small>{t`common.noInformation`}</Small>
        )}
        {!isEditMode && (
          <SelectedOptionsListAsLogo
            list={mapOrganizationClientsToOptions(clients)}
            listRef={listRef}
            height={!isExpanded ? maxHeight : undefined}
            isListExpanded={isExpanded}
          />
        )}
        {isEditMode && (
          <AsyncSelectWithList
            defaultValue={mapOrganizationClientsToOptions(clients)}
            setOptions={setOptions}
            placeholder={t`default:search`}
            hideSelectedOptions={false}
            components={{ Option: OptionWithLogo }}
            SelectedOptionsListComponent={<SelectedOptionsListAsLogo />}
            onChange={newValue => {
              if (!Array.isArray(newValue)) return;
              setDraftOptions(prev => mergeClientOptions(newValue, prev));
            }}
          />
        )}
      </Container>
    </OutsideClickHandler>
  );
}

const Container = styled(Widget)`
  min-width: 439px;
  display: flex;
  flex-direction: column;
  gap: ${WIDGET_CONTAINER_GAP}px;
`;

const HeaderContainer = styled(Header)`
  margin: ${WIDGET_HEADER_MARGIN};
`;
